import { Boxes, Check, LogIn, Settings, User as UserIcon } from 'lucide-react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NotificationsBell } from '@/components/notifications/notifications-bell'
import { cn } from '@/lib/utils'
import { useMe } from '@/features/users/use-users'

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'text-sm font-medium transition-colors hover:text-foreground',
    isActive ? 'text-foreground' : 'text-muted-foreground'
  )

export const NavBar = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const isAuthenticated = auth.isAuthenticated
  const { data: me } = useMe()

  return (
    <nav className="border-b border-border bg-background">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to={isAuthenticated ? '/dashboard' : '/login'} className="flex items-center gap-2">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground"
            aria-hidden
          >
            <Check className="h-5 w-5" strokeWidth={3} />
          </span>
          <span className="hidden text-xl font-semibold sm:inline">ToDone</span>
        </Link>
        <div className="flex items-center gap-3 sm:gap-6">
          <div className="flex items-center gap-4 sm:gap-6">
            <NavLink to="/browse" className={navLinkClass}>
              Browse
            </NavLink>
            <NavLink to="/c" className={navLinkClass}>
              Communities
            </NavLink>
            {isAuthenticated && (
              <NavLink to="/dashboard" className={navLinkClass}>
                Projects
              </NavLink>
            )}
          </div>
          {isAuthenticated && <NotificationsBell />}
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Account menu"
                  className="h-9 w-9 overflow-hidden rounded-full p-0"
                >
                  {me?.avatarUrl ? (
                    <img
                      src={me.avatarUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <UserIcon className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {me && (
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-sm font-medium leading-tight">{me.displayName}</div>
                    {me.handle && <div className="text-xs text-muted-foreground">@{me.handle}</div>}
                  </DropdownMenuLabel>
                )}
                <DropdownMenuSeparator />
                {me?.handle && (
                  <DropdownMenuItem onSelect={() => navigate(`/u/${me.handle}`)}>
                    <UserIcon className="mr-2 h-4 w-4" />
                    View profile
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => navigate('/me/inventory')}>
                  <Boxes className="mr-2 h-4 w-4" />
                  Inventory
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild variant="outline" size="sm" className="gap-2">
              <Link to="/login">
                <LogIn className="h-4 w-4" />
                Sign in
              </Link>
            </Button>
          )}
        </div>
      </div>
    </nav>
  )
}
